import React, { useEffect, useState, useRef, createElement } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { AnchorIcon, Compass, Waves, Map, LifeBuoy, Droplet, ShoppingBagIcon } from 'lucide-react';
export const Landing: React.FC = () => {
  const bubblesRef = useRef<HTMLDivElement>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  useEffect(() => {
    // Mark as loaded after a short delay for animation purposes
    const timer = setTimeout(() => {
      setIsLoaded(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);
  useEffect(() => {
    // Create bubbles animation
    if (!bubblesRef.current) return;
    const createBubble = () => {
      const bubble = document.createElement('div');
      bubble.className = 'bubble';
      // Random size between 10px and 60px
      const size = Math.random() * 50 + 10;
      bubble.style.width = `${size}px`;
      bubble.style.height = `${size}px`;
      // Random position along the width
      bubble.style.left = `${Math.random() * 100}%`;
      // Random delay for the animation
      bubble.style.animationDuration = `${Math.random() * 5 + 3}s`;
      // Opacity based on size for a more realistic look
      bubble.style.opacity = `${Math.min(size / 60, 0.8)}`;
      bubblesRef.current?.appendChild(bubble);
      // Remove bubble after animation completes
      setTimeout(() => {
        bubble.remove();
      }, 8000);
    };
    // Create bubbles at random intervals
    const interval = setInterval(() => {
      for (let i = 0; i < 3; i++) {
        createBubble();
      }
    }, 1000);
    return () => clearInterval(interval);
  }, []);
  return <div className="h-screen w-full overflow-hidden bg-gradient-to-b from-primary-dark via-primary to-accent relative" data-id="element-1030">
      {/* Underwater light rays effect */}
      <div className="absolute inset-0 z-0 overflow-hidden" data-id="element-1031">
        <div className="light-rays" data-id="element-1032"></div>
      </div>
      {/* Bubbles container */}
      <div ref={bubblesRef} className="bubbles-container absolute inset-0 z-0 overflow-hidden" data-id="element-1033"></div>
      {/* Fish animation */}
      <div className="fish-container" data-id="element-1034">
        <div className="fish fish-1" data-id="element-1035"></div>
        <div className="fish fish-2" data-id="element-1036"></div>
        <div className="fish fish-3" data-id="element-1037"></div>
        <div className="fish fish-4" data-id="element-1038"></div>
        <div className="fish fish-5" data-id="element-1039"></div>
      </div>
      {/* Floating seaweed */}
      <div className="seaweed-container" data-id="element-1040">
        <div className="seaweed seaweed-1" data-id="element-1041"></div>
        <div className="seaweed seaweed-2" data-id="element-1042"></div>
        <div className="seaweed seaweed-3" data-id="element-1043"></div>
      </div>
      {/* Content */}
      <div className={`relative z-10 flex h-full flex-col items-center justify-center px-4 text-white transition-opacity duration-1000 ${isLoaded ? 'opacity-100' : 'opacity-0'}`} data-id="element-1044">
        <div className="mb-4 sm:mb-8 flex items-center" data-id="element-1045">
          <div className="logo-container relative mr-4" data-id="element-1046">
            <div className="logo-circle absolute inset-0 bg-white rounded-full opacity-20 animate-pulse" data-id="element-1047"></div>
            <div className="logo-inner relative z-10 bg-white/10 backdrop-blur-sm p-4 rounded-full shadow-glow" data-id="element-1048">
              <AnchorIcon size={40} className="text-white" data-id="element-1049" />
            </div>
          </div>
          <div data-id="element-1050">
            <h1 className="text-4xl sm:text-6xl font-bold tracking-tight" data-id="element-1051">
              <span className="text-white" data-id="element-1052">Diver</span>
              <span className="text-secondary" data-id="element-1053">gram</span>
            </h1>
            <div className="h-1 w-24 bg-secondary rounded-full mt-2" data-id="element-1054"></div>
          </div>
        </div>
        <h2 className="text-2xl sm:text-3xl font-light text-center mb-4 max-w-lg" data-id="element-1055">
          Dive deeper.{' '}
          <span className="text-secondary font-normal" data-id="element-1056">Connect</span> wider.
        </h2>
        <p className="mb-8 sm:mb-12 max-w-md text-center text-base sm:text-xl font-light leading-relaxed" data-id="element-1057">
          Your digital diving companion. Track dives, connect with other divers,
          and explore the underwater world like never before.
        </p>
        <div className="flex flex-col space-y-4 sm:flex-row sm:space-x-6 sm:space-y-0 max-w-md w-full px-4" data-id="element-1058">
          <Link to="/login" className="w-full transform transition-transform duration-300 hover:scale-105" data-id="element-1059">
            <Button variant="outline" size="lg" fullWidth className="bg-white text-primary-dark hover:bg-white/90 border-2 border-white backdrop-blur-sm shadow-glow-white font-bold !text-primary-dark" data-id="element-1060">
              Log In
            </Button>
          </Link>
          <Link to="/register" className="w-full transform transition-transform duration-300 hover:scale-105" data-id="element-1061">
            <Button variant="secondary" size="lg" fullWidth className="backdrop-blur-sm shadow-glow-orange border-2 border-secondary" data-id="element-1062">
              Sign Up
            </Button>
          </Link>
        </div>
        {/* Feature highlights */}
        <div className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-8 max-w-2xl" data-id="element-1063">
          <div className="feature-card text-center" data-id="element-1064">
            <div className="icon-container mx-auto mb-2 flex items-center justify-center w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm" data-id="element-1065">
              <Map size={20} className="text-white" data-id="element-1066" />
            </div>
            <p className="text-sm font-medium" data-id="element-1067">Track Dives</p>
          </div>
          <div className="feature-card text-center" data-id="element-1068">
            <div className="icon-container mx-auto mb-2 flex items-center justify-center w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm" data-id="element-1069">
              <Compass size={20} className="text-white" data-id="element-1070" />
            </div>
            <p className="text-sm font-medium" data-id="element-1071">Discover Spots</p>
          </div>
          <div className="feature-card text-center" data-id="element-1072">
            <div className="icon-container mx-auto mb-2 flex items-center justify-center w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm" data-id="element-1073">
              <LifeBuoy size={20} className="text-white" data-id="element-1074" />
            </div>
            <p className="text-sm font-medium" data-id="element-1075">Connect with Divers</p>
          </div>
          <div className="feature-card text-center" data-id="element-1076">
            <div className="icon-container mx-auto mb-2 flex items-center justify-center w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm" data-id="element-1077">
              <ShoppingBagIcon size={20} className="text-white" data-id="element-1078" />
            </div>
            <p className="text-sm font-medium" data-id="element-1079">Shop Equipment</p>
          </div>
        </div>
      </div>
      {/* Wave effect at the bottom */}
      <div className="absolute bottom-0 left-0 w-full z-10" data-id="element-1080">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 320" className="w-full" data-id="element-1081">
          <path fill="#ffffff" fillOpacity="0.2" d="M0,192L48,186.7C96,181,192,171,288,181.3C384,192,480,224,576,229.3C672,235,768,213,864,181.3C960,149,1056,107,1152,96C1248,85,1344,107,1392,117.3L1440,128L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z" data-id="element-1082"></path>
          <path fill="#ffffff" fillOpacity="0.3" d="M0,224L48,213.3C96,203,192,181,288,181.3C384,181,480,203,576,197.3C672,192,768,160,864,165.3C960,171,1056,213,1152,218.7C1248,224,1344,192,1392,176L1440,160L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z" data-id="element-1083"></path>
        </svg>
      </div>
      <style jsx data-id="element-1084">{`
        .bubbles-container {
          pointer-events: none;
        }
        .bubble {
          position: absolute;
          bottom: -50px;
          background-color: rgba(255, 255, 255, 0.2);
          border-radius: 50%;
          animation: rise linear forwards;
          backdrop-filter: blur(1px);
          border: 1px solid rgba(255, 255, 255, 0.1);
        }
        @keyframes rise {
          0% {
            transform: translateY(0) scale(1);
            opacity: 0;
          }
          20% {
            opacity: 1;
          }
          80% {
            opacity: 0.7;
          }
          100% {
            transform: translateY(-100vh) scale(1.5);
            opacity: 0;
          }
        }
        .fish-container {
          position: absolute;
          width: 100%;
          height: 100%;
          overflow: hidden;
          pointer-events: none;
        }
        .fish {
          position: absolute;
          background-size: contain;
          background-repeat: no-repeat;
          background-position: center;
          opacity: 0.7;
          filter: drop-shadow(0 0 8px rgba(0, 0, 0, 0.2));
        }
        .fish-1 {
          width: 120px;
          height: 80px;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 512 512'%3E%3Cpath fill='%23ffffff' d='M256 219.978c-20.374 0-36.978 16.604-36.978 36.978s16.604 36.978 36.978 36.978 36.978-16.604 36.978-36.978-16.604-36.978-36.978-36.978zm-6.154 48.132c-6.113 0-11.154-5.04-11.154-11.154s5.04-11.154 11.154-11.154 11.154 5.04 11.154 11.154-5.04 11.154-11.154 11.154zm212.088-90.264c-27.994-27.994-73.292-27.994-101.286 0-27.994-27.994-73.292-27.994-101.286 0-27.994-27.994-73.292-27.994-101.286 0-27.994-27.994-73.292-27.994-101.286 0-27.994 27.994-27.994 73.292 0 101.286-27.994 27.994-27.994 73.292 0 101.286 27.994 27.994 73.292 27.994 101.286 0 27.994 27.994 73.292 27.994 101.286 0 27.994 27.994 73.292 27.994 101.286 0 27.994 27.994 27.994 73.292 0 101.286-27.994 27.994-27.994 73.292 0 101.286 13.997 13.997 32.335 20.996 50.643 20.996s36.646-6.998 50.643-20.996c27.994-27.994 27.994-73.292 0-101.286 27.994-27.994 27.994-73.292 0-101.286 27.994-27.994 27.994-73.292 0-101.286z'%3E%3C/path%3E%3C/svg%3E");
          top: 30%;
          left: -120px;
          animation: fishSwim1 25s linear infinite;
        }
        .fish-2 {
          width: 80px;
          height: 60px;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 512 512'%3E%3Cpath fill='%23ffffff' d='M256 219.978c-20.374 0-36.978 16.604-36.978 36.978s16.604 36.978 36.978 36.978 36.978-16.604 36.978-36.978-16.604-36.978-36.978-36.978zm-6.154 48.132c-6.113 0-11.154-5.04-11.154-11.154s5.04-11.154 11.154-11.154 11.154 5.04 11.154 11.154-5.04 11.154-11.154 11.154zm212.088-90.264c-27.994-27.994-73.292-27.994-101.286 0-27.994-27.994-73.292-27.994-101.286 0-27.994-27.994-73.292-27.994-101.286 0-27.994-27.994-73.292-27.994-101.286 0-27.994 27.994-27.994 73.292 0 101.286-27.994 27.994-27.994 73.292 0 101.286 27.994 27.994 73.292 27.994 101.286 0 27.994 27.994 73.292 27.994 101.286 0 27.994 27.994 73.292 27.994 101.286 0 27.994 27.994 27.994 73.292 0 101.286-27.994 27.994-27.994 73.292 0 101.286 13.997 13.997 32.335 20.996 50.643 20.996s36.646-6.998 50.643-20.996c27.994-27.994 27.994-73.292 0-101.286 27.994-27.994 27.994-73.292 0-101.286 27.994-27.994 27.994-73.292 0-101.286z'%3E%3C/path%3E%3C/svg%3E");
          top: 60%;
          left: -80px;
          animation: fishSwim2 18s linear infinite;
        }
        .fish-3 {
          width: 100px;
          height: 70px;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 512 512'%3E%3Cpath fill='%23ffffff' d='M256 219.978c-20.374 0-36.978 16.604-36.978 36.978s16.604 36.978 36.978 36.978 36.978-16.604 36.978-36.978-16.604-36.978-36.978-36.978zm-6.154 48.132c-6.113 0-11.154-5.04-11.154-11.154s5.04-11.154 11.154-11.154 11.154 5.04 11.154 11.154-5.04 11.154-11.154 11.154zm212.088-90.264c-27.994-27.994-73.292-27.994-101.286 0-27.994-27.994-73.292-27.994-101.286 0-27.994-27.994-73.292-27.994-101.286 0-27.994-27.994-73.292-27.994-101.286 0-27.994 27.994-27.994 73.292 0 101.286-27.994 27.994-27.994 73.292 0 101.286 27.994 27.994 73.292 27.994 101.286 0 27.994 27.994 73.292 27.994 101.286 0 27.994 27.994 73.292 27.994 101.286 0 27.994 27.994 27.994 73.292 0 101.286-27.994 27.994-27.994 73.292 0 101.286 13.997 13.997 32.335 20.996 50.643 20.996s36.646-6.998 50.643-20.996c27.994-27.994 27.994-73.292 0-101.286 27.994-27.994 27.994-73.292 0-101.286 27.994-27.994 27.994-73.292 0-101.286z'%3E%3C/path%3E%3C/svg%3E");
          top: 40%;
          left: -100px;
          animation: fishSwim3 30s linear infinite;
        }
        .fish-4 {
          width: 60px;
          height: 40px;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 512 512'%3E%3Cpath fill='%23ffffff' d='M256 219.978c-20.374 0-36.978 16.604-36.978 36.978s16.604 36.978 36.978 36.978 36.978-16.604 36.978-36.978-16.604-36.978-36.978-36.978zm-6.154 48.132c-6.113 0-11.154-5.04-11.154-11.154s5.04-11.154 11.154-11.154 11.154 5.04 11.154 11.154-5.04 11.154-11.154 11.154zm212.088-90.264c-27.994-27.994-73.292-27.994-101.286 0-27.994-27.994-73.292-27.994-101.286 0-27.994-27.994-73.292-27.994-101.286 0-27.994-27.994-73.292-27.994-101.286 0-27.994 27.994-27.994 73.292 0 101.286-27.994 27.994-27.994 73.292 0 101.286 27.994 27.994 73.292 27.994 101.286 0 27.994 27.994 73.292 27.994 101.286 0 27.994 27.994 73.292 27.994 101.286 0 27.994 27.994 27.994 73.292 0 101.286-27.994 27.994-27.994 73.292 0 101.286 13.997 13.997 32.335 20.996 50.643 20.996s36.646-6.998 50.643-20.996c27.994-27.994 27.994-73.292 0-101.286 27.994-27.994 27.994-73.292 0-101.286 27.994-27.994 27.994-73.292 0-101.286z'%3E%3C/path%3E%3C/svg%3E");
          top: 20%;
          left: -60px;
          animation: fishSwim4 15s linear infinite;
        }
        .fish-5 {
          width: 90px;
          height: 60px;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 512 512'%3E%3Cpath fill='%23ffffff' d='M256 219.978c-20.374 0-36.978 16.604-36.978 36.978s16.604 36.978 36.978 36.978 36.978-16.604 36.978-36.978-16.604-36.978-36.978-36.978zm-6.154 48.132c-6.113 0-11.154-5.04-11.154-11.154s5.04-11.154 11.154-11.154 11.154 5.04 11.154 11.154-5.04 11.154-11.154 11.154zm212.088-90.264c-27.994-27.994-73.292-27.994-101.286 0-27.994-27.994-73.292-27.994-101.286 0-27.994-27.994-73.292-27.994-101.286 0-27.994-27.994-73.292-27.994-101.286 0-27.994 27.994-27.994 73.292 0 101.286-27.994 27.994-27.994 73.292 0 101.286 27.994 27.994 73.292 27.994 101.286 0 27.994 27.994 73.292 27.994 101.286 0 27.994 27.994 73.292 27.994 101.286 0 27.994 27.994 27.994 73.292 0 101.286-27.994 27.994-27.994 73.292 0 101.286 13.997 13.997 32.335 20.996 50.643 20.996s36.646-6.998 50.643-20.996c27.994-27.994 27.994-73.292 0-101.286 27.994-27.994 27.994-73.292 0-101.286 27.994-27.994 27.994-73.292 0-101.286z'%3E%3C/path%3E%3C/svg%3E");
          top: 70%;
          left: -90px;
          animation: fishSwim5 22s linear infinite;
        }
        .seaweed-container {
          position: absolute;
          bottom: 0;
          width: 100%;
          height: 100%;
          overflow: hidden;
          pointer-events: none;
          z-index: 1;
        }
        .seaweed {
          position: absolute;
          bottom: 0;
          background-repeat: no-repeat;
          background-position: bottom center;
          filter: blur(1px);
        }
        .seaweed-1 {
          width: 100px;
          height: 200px;
          left: 5%;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 300'%3E%3Cpath fill='%2300BFA5' opacity='0.5' d='M50,0 C30,50 70,100 50,150 C30,200 70,250 50,300 L60,300 C80,250 40,200 60,150 C80,100 40,50 60,0 L50,0 Z'%3E%3C/path%3E%3C/svg%3E");
          animation: seaweedSway 5s ease-in-out infinite;
        }
        .seaweed-2 {
          width: 80px;
          height: 180px;
          left: 15%;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 300'%3E%3Cpath fill='%2300BFA5' opacity='0.5' d='M50,0 C30,50 70,100 50,150 C30,200 70,250 50,300 L60,300 C80,250 40,200 60,150 C80,100 40,50 60,0 L50,0 Z'%3E%3C/path%3E%3C/svg%3E");
          animation: seaweedSway 7s ease-in-out infinite reverse;
        }
        .seaweed-3 {
          width: 120px;
          height: 220px;
          right: 10%;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 300'%3E%3Cpath fill='%2300BFA5' opacity='0.5' d='M50,0 C30,50 70,100 50,150 C30,200 70,250 50,300 L60,300 C80,250 40,200 60,150 C80,100 40,50 60,0 L50,0 Z'%3E%3C/path%3E%3C/svg%3E");
          animation: seaweedSway 6s ease-in-out infinite;
        }
        .light-rays {
          position: absolute;
          top: -50%;
          left: 0;
          right: 0;
          height: 200%;
          background: radial-gradient(
            ellipse at top,
            rgba(255, 255, 255, 0.2) 0%,
            rgba(255, 255, 255, 0) 70%
          );
          animation: lightRays 8s ease-in-out infinite;
        }
        @keyframes fishSwim1 {
          0% {
            left: -120px;
            transform: scaleX(1);
          }
          49% {
            transform: scaleX(1);
          }
          50% {
            left: 100%;
            transform: scaleX(-1);
          }
          99% {
            transform: scaleX(-1);
          }
          100% {
            left: -120px;
            transform: scaleX(1);
          }
        }
        @keyframes fishSwim2 {
          0% {
            left: -80px;
            transform: scaleX(1);
          }
          49% {
            transform: scaleX(1);
          }
          50% {
            left: 100%;
            transform: scaleX(-1);
          }
          99% {
            transform: scaleX(-1);
          }
          100% {
            left: -80px;
            transform: scaleX(1);
          }
        }
        @keyframes fishSwim3 {
          0% {
            left: -100px;
            transform: scaleX(1) translateY(0px);
          }
          25% {
            transform: scaleX(1) translateY(20px);
          }
          49% {
            transform: scaleX(1) translateY(0px);
          }
          50% {
            left: 100%;
            transform: scaleX(-1) translateY(0px);
          }
          75% {
            transform: scaleX(-1) translateY(-20px);
          }
          99% {
            transform: scaleX(-1) translateY(0px);
          }
          100% {
            left: -100px;
            transform: scaleX(1) translateY(0px);
          }
        }
        @keyframes fishSwim4 {
          0% {
            left: -60px;
            transform: scaleX(1) translateY(0px);
          }
          25% {
            transform: scaleX(1) translateY(-15px);
          }
          49% {
            transform: scaleX(1) translateY(0px);
          }
          50% {
            left: 100%;
            transform: scaleX(-1) translateY(0px);
          }
          75% {
            transform: scaleX(-1) translateY(15px);
          }
          99% {
            transform: scaleX(-1) translateY(0px);
          }
          100% {
            left: -60px;
            transform: scaleX(1) translateY(0px);
          }
        }
        @keyframes fishSwim5 {
          0% {
            left: -90px;
            transform: scaleX(1) translateY(0px);
          }
          25% {
            transform: scaleX(1) translateY(10px);
          }
          49% {
            transform: scaleX(1) translateY(0px);
          }
          50% {
            left: 100%;
            transform: scaleX(-1) translateY(0px);
          }
          75% {
            transform: scaleX(-1) translateY(-10px);
          }
          99% {
            transform: scaleX(-1) translateY(0px);
          }
          100% {
            left: -90px;
            transform: scaleX(1) translateY(0px);
          }
        }
        @keyframes seaweedSway {
          0% {
            transform: rotate(0deg);
          }
          50% {
            transform: rotate(5deg);
          }
          100% {
            transform: rotate(0deg);
          }
        }
        @keyframes lightRays {
          0% {
            opacity: 0.5;
            transform: rotate(0deg);
          }
          50% {
            opacity: 0.7;
            transform: rotate(180deg);
          }
          100% {
            opacity: 0.5;
            transform: rotate(360deg);
          }
        }
        .shadow-glow {
          box-shadow: 0 0 20px rgba(255, 255, 255, 0.3);
        }
        .shadow-glow-white {
          box-shadow: 0 0 20px rgba(255, 255, 255, 0.3);
        }
        .shadow-glow-orange {
          box-shadow: 0 0 20px rgba(255, 107, 53, 0.4);
        }
        .feature-card {
          transition: transform 0.3s ease;
        }
        .feature-card:hover {
          transform: translateY(-5px);
        }
        .icon-container {
          transition: all 0.3s ease;
        }
        .feature-card:hover .icon-container {
          background-color: rgba(255, 255, 255, 0.2);
          transform: scale(1.1);
        }
      `}</style>
    </div>;
};